import { type HTMLAttributes, type ReactNode, useRef, useState } from "react";
import { useMove } from "react-aria";
import { createPortal } from "react-dom";

export interface UsePanelResizeProps {
  /** Current width of the panel, in pixels. */
  width: number;
  minWidth: number;
  maxWidth: number;
  onWidthChange: (width: number) => void;
  /**
   * Edge of the panel the handle sits on. Dragging away from the panel widens
   * it, so a `"left"` handle widens as the pointer moves left.
   */
  edge: "left" | "right";
  /** Accessible name of the separator, e.g. `"Resize chat panel"`. */
  label: string;
  /** Pixels the width changes per arrow key press. */
  keyboardStep?: number;
}

export interface UsePanelResizeResult {
  /** Spread on the drag handle element. */
  separatorProps: HTMLAttributes<HTMLElement>;
  isResizing: boolean;
  /**
   * Window-wide layer that keeps the pointer over the drag interaction for the
   * whole drag. Render it anywhere; it portals to the document body.
   */
  resizeOverlay: ReactNode;
}

function clampWidth(width: number, minWidth: number, maxWidth: number) {
  return Math.min(maxWidth, Math.max(minWidth, width));
}

/**
 * Turns an element into a keyboard- and pointer-operable resize separator for
 * a panel docked to one side of the viewport.
 *
 * Pointer tracking comes from react-aria's `useMove`, which registers its move
 * listeners on the window synchronously on pointer down — so a fast drag can't
 * outrun the handle — and normalizes touch and arrow-key interaction. The
 * accompanying overlay covers the window while dragging, so hit-testing can't
 * hand the pointer to an iframe or embedded plugin mid-drag and the resize
 * cursor stays put over any element the pointer crosses.
 */
export function usePanelResize({
  width,
  minWidth,
  maxWidth,
  onWidthChange,
  edge,
  label,
  keyboardStep = 16,
}: UsePanelResizeProps): UsePanelResizeResult {
  const [isResizing, setIsResizing] = useState(false);
  // Where the drag began and how far it has travelled since, unclamped. Refs
  // because several moves can be processed before React re-renders with the
  // new width, and unclamped so that dragging past a limit and back returns
  // the handle to the pointer instead of leaving it trailing by the overshoot.
  const dragRef = useRef({ startWidth: width, offset: 0 });

  const widenDirection = edge === "left" ? -1 : 1;

  const { moveProps } = useMove({
    onMoveStart: () => {
      dragRef.current = { startWidth: width, offset: 0 };
      setIsResizing(true);
    },
    onMove: ({ deltaX, pointerType }) => {
      const drag = dragRef.current;
      const step = pointerType === "keyboard" ? keyboardStep : 1;
      drag.offset += widenDirection * deltaX * step;
      onWidthChange(
        clampWidth(drag.startWidth + drag.offset, minWidth, maxWidth),
      );
    },
    onMoveEnd: () => setIsResizing(false),
  });

  return {
    isResizing,
    separatorProps: {
      ...moveProps,
      role: "separator",
      "aria-orientation": "vertical",
      "aria-label": label,
      "aria-valuenow": Math.round(width),
      "aria-valuemin": minWidth,
      "aria-valuemax": maxWidth,
      tabIndex: 0,
    },
    resizeOverlay: isResizing
      ? createPortal(
          <div className="fixed inset-0 z-50 cursor-col-resize" />,
          document.body,
        )
      : null,
  };
}
